import type { FitVerdict } from '@pairing/core/capabilities/fit';
import type { LeadItem, PageMeta } from './types';

/** The fit check page at /fit-check. Verdict keys are the existing tool's (FIT_VERDICTS in @pairing/core). */
export const fitCheck = {
  meta: {
    path: '/fit-check',
    name: 'Can Pairing handle it?',
    title: 'Can Pairing handle it? | Pairing by Denamico',
    description:
      'Describe a HubSpot integration in plain words and get an honest verdict, the matching capabilities, the limits and the questions to ask next. No sign-in.',
    ogImage: '/og/fit-check.png',
  } satisfies PageMeta,

  hero: {
    headline: 'Can Pairing handle it?',
    subhead:
      "A fit check that's allowed to say no. Describe the systems and what you heard, and you'll get a straight answer plus the questions to ask next. No sign-in.",
    inputLabel: 'Describe the integration',
    placeholder:
      'A manufacturer uses Salesforce for sales and HubSpot for marketing. Their admin edits the Industry picklist often and lists keep breaking. About 40,000 contacts.',
    chipsLabel: 'Add a system',
    chips: ['Salesforce', 'NetSuite', 'Custom REST API', 'Not sure'],
    button: 'Check fit',
    hint: "Describe systems and problems, not customer records, and don't paste names, emails or other personal data. Your text is stored for 180 days so Denamico can improve the check, and it is sent to Anthropic to be read, including web searches for the systems you name.",
  },

  /** How a result reads. Headings come from "What comes back"; states are the brief's own words. */
  result: {
    heading: 'Result',
    verdictLabel: 'Verdict',
    capabilities: 'Capabilities that match',
    limits: 'The honest limits',
    questions: 'Questions to ask next',
    nextStep: 'Next step',
    caveats: 'About this answer',
    loading: 'Checking, and looking up the systems you named. This can take up to half a minute.',
    error: "The fit check didn't respond. Try again, or email us the details.",
    empty: 'Describe the integration first.',
    matchedOn: 'Matched on',
    /** An off_topic answer: no capabilities or limits, just a nudge back to describing an integration. */
    offTopicLabel: 'Off topic',
    offTopicQuestions: 'Try describing',
    /** The systems the check looked up on the web (ADR 0007 R1a). */
    research: 'What we found about each system',
    sources: 'Sources',
    checkedOn: 'Looked up',
    api: { rest_json: 'REST API (JSON)', other_api: 'An API, but not REST with JSON', none: 'No public API found', unknown: "Couldn't confirm an API" },
    webhooks: { yes: 'Sends webhooks', no: 'No webhooks', unknown: 'Webhooks not confirmed' },
  },

  /** The popup after a visitor's fifth check in one browser session. It doesn't block more checks. */
  talk: {
    afterChecks: 5,
    title: 'Ready to talk it through?',
    body: "You've run a few checks. Denamico can look at the whole picture with you and say what it would take. Tell us a little and we'll be in touch.",
    keepChecking: 'Keep checking',
  },

  /** The existing "ask a person" follow-up (POST /v1/fit/review), kept from the old /fit page for needs-review answers. */
  review: {
    heading: 'Want a person to look?',
    body: 'Sends this question to Denamico, and the question is stored like any other check, for 180 days. Leave an email if you want a reply: it is used only to answer you and is deleted after 30 days.',
    emailLabel: 'Your email (optional)',
    button: 'Ask Denamico to follow up',
    sent: 'Sent to Denamico',
  },

  whatComesBack: {
    headline: 'What comes back',
    list: [
      'A verdict, from the five below',
      'The Pairing capabilities that match what you described',
      "The honest limits: what it won't do here",
      'Questions to ask next, so the follow-up call goes further',
      'A next step that fits the verdict',
    ],
  },

  verdicts: {
    headline: 'The five verdicts',
    caption: 'The five verdicts the fit check can return',
    columns: ['Verdict', 'What it means'],
    rows: [
      { key: 'fits', label: 'Fits', meaning: "Pairing covers this with what's built today." },
      { key: 'likely_fits', label: 'Likely fits', meaning: 'Probably, with a detail or two to confirm. The questions to ask next tell you which.' },
      {
        key: 'needs_review',
        label: 'Needs review',
        meaning: "We don't know enough yet, often because the system is new to us. Someone at Denamico will take a look rather than guess.",
      },
      { key: 'partial', label: 'Partial', meaning: 'Pairing covers part of it. Another tool or approach covers the rest, and the answer says which part is which.' },
      { key: 'not_a_fit', label: 'Not a fit', meaning: "Pairing isn't the right tool, and the answer says why." },
    ] satisfies Array<{ key: FitVerdict; label: string; meaning: string }>,
  },

  ways: {
    headline: 'Two ways to use it',
    here: { lead: 'Here,', body: 'in the box above.' } satisfies LeadItem,
    web: { lead: 'For AI tools that read the web,', body: 'at pairing.denamico.com/llms.txt.', link: { label: 'pairing.denamico.com/llms.txt', href: '/llms.txt' } } satisfies LeadItem,
  },

  stops: {
    headline: 'Where Pairing stops',
    list: [
      "HubSpot's native integration already covers it. Use that; it's free and HubSpot supports it.",
      'The other system has no API, and no SQL Server database or SFTP or FTPS files to read: EDI or a mainframe. SQL Server and files (read and written) are in pilot; plain FTP and other databases need review.',
      'The process spans three or more systems, or needs people to approve steps along the way.',
      'The volume is warehouse-scale.',
      "HubSpot isn't one of the systems.",
    ],
  },

  nextSteps: {
    headline: 'What to do with the answer',
    items: {
      reps: { lead: 'HubSpot reps with a fit:', body: 'add Denamico to the deal to get early access to Demo Studio.', link: { label: 'add Denamico to the deal', href: '/reps#access' } },
      teams: { lead: 'Teams with a fit:', body: 'talk to Denamico about setup.', link: { label: 'talk to Denamico', href: '/#contact' } },
      review: { lead: 'Needs review:', body: 'send it to us and someone at Denamico will look.', link: { label: 'send it to us', href: '/#contact' } },
      notFit: { lead: 'Not a fit:', body: 'the answer says why, and points you to HubSpot\'s own integration when one covers it. No hard feelings.' },
    } satisfies Record<string, LeadItem>,
  },
} as const;

type NextStepKey = keyof typeof fitCheck.nextSteps.items;

/**
 * The "What to do with the answer" entries a verdict shows under its result. Partial has no entry of its own in the
 * copy: it gets the teams and review entries, since part of it fits and part needs a look.
 */
export const NEXT_STEPS_BY_VERDICT: Record<FitVerdict, NextStepKey[]> = {
  fits: ['reps', 'teams'],
  likely_fits: ['reps', 'teams'],
  needs_review: ['review'],
  partial: ['teams', 'review'],
  not_a_fit: ['notFit'],
  off_topic: [],
};

/** off_topic isn't one of the five verdicts the page explains, so its label lives with the result copy. */
export const verdictLabel = (v: FitVerdict): string =>
  v === 'off_topic' ? fitCheck.result.offTopicLabel : fitCheck.verdicts.rows.find((r) => r.key === v)!.label;
